'use client';

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Search, ChevronUp, ChevronDown, Eye, Edit3, Trash2, Image as ImageIcon, Star, SlidersHorizontal, CheckSquare, X, Calendar, Tag, TrendingUp, ArrowUpDown, ChevronDown as ChevronDownIcon, BarChart2 } from 'lucide-react';
import { toast } from 'sonner';
import { StoredTrade, loadTradesAsync, deleteTradeAsync, deleteTradesBulkAsync } from '../utils/tradeStorage';
import TradeDetailModal from './TradeDetailModal';
import LogTradeModal from './LogTradeModal';

const ALL_STRATEGIES = ['Breakout', 'Momentum', 'VWAP Reclaim', 'Gap Fill', 'Mean Reversion'];
const ALL_DIRECTIONS = ['Long', 'Short'] as const;
const ALL_OUTCOMES = ['Reviewed', 'Pending Review', 'Flagged'] as const;

const strategyColors: Record<string, string> = {
  'Breakout': 'bg-neutral-subtle text-neutral-400',
  'Momentum': 'bg-primary/10 text-primary',
  'VWAP Reclaim': 'bg-warning-subtle text-warning',
  'Gap Fill': 'bg-purple-900/30 text-purple-400',
  'Mean Reversion': 'bg-negative-subtle text-negative',
};

const statusColors: Record<string, string> = {
  'Reviewed': 'bg-positive-subtle text-positive border border-positive/20',
  'Pending Review': 'bg-warning-subtle text-warning border border-warning/20',
  'Flagged': 'bg-negative-subtle text-negative border border-negative/20',
};

function StarRating({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={`star-${star}`}
          size={10}
          className={star <= rating ? 'text-warning fill-warning' : 'text-muted'}
        />
      ))}
    </div>
  );
}

interface ActiveFilters {
  dateFrom: string;
  dateTo: string;
  strategies: string[];       // multi-select
  directions: string[];       // multi-select
  outcomes: string[];         // multi-select (status)
  pnlMin: string;             // P&L range slider
  pnlMax: string;
  rrMin: string;              // R:R range
  rrMax: string;
}

const defaultFilters: ActiveFilters = {
  dateFrom: '',
  dateTo: '',
  strategies: [],
  directions: [],
  outcomes: [],
  pnlMin: '',
  pnlMax: '',
  rrMin: '',
  rrMax: '',
};

const FILTER_STORAGE_KEY = 'tradelog_filters_v2';

function loadPersistedFilters(): ActiveFilters {
  if (typeof window === 'undefined') return defaultFilters;
  try {
    const raw = localStorage.getItem(FILTER_STORAGE_KEY);
    if (!raw) return defaultFilters;
    const parsed = JSON.parse(raw);
    return { ...defaultFilters, ...parsed };
  } catch {
    return defaultFilters;
  }
}

function saveFilters(filters: ActiveFilters) {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filters));
  } catch { /* ignore */ }
}

// Multi-select dropdown component
interface MultiSelectProps {
  label: string;
  icon: React.ReactNode;
  options: readonly string[];
  selected: string[];
  onChange: (values: string[]) => void;
  colorMap?: Record<string, string>;
}

function MultiSelectDropdown({ label, icon, options, selected, onChange, colorMap }: MultiSelectProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const toggle = (val: string) => {
    if (selected.includes(val)) onChange(selected.filter((v) => v !== val));
    else onChange([...selected, val]);
  };

  const hasSelection = selected.length > 0;

  return (
    <div className="flex flex-col gap-1 relative" ref={ref}>
      <label className="flex items-center gap-1 text-xs font-500 text-muted-foreground">
        {icon}
        {label}
      </label>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs border transition-all min-w-[130px] ${
          hasSelection
            ? 'bg-primary/10 border-primary/40 text-primary' :'bg-card border-border text-muted-foreground hover:text-foreground hover:border-border/80'
        }`}
      >
        <span className="flex-1 text-left truncate">
          {hasSelection ? `${selected.length} selected` : `All ${label}`}
        </span>
        <ChevronDownIcon size={11} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 z-50 bg-card border border-border rounded-xl shadow-lg min-w-[160px] py-1 overflow-hidden">
          {options.map((opt) => {
            const isSelected = selected.includes(opt);
            return (
              <button
                key={opt}
                type="button"
                onClick={() => toggle(opt)}
                className={`w-full flex items-center gap-2 px-3 py-2 text-xs transition-colors text-left ${
                  isSelected ? 'bg-primary/10 text-primary' : 'text-foreground hover:bg-muted/60'
                }`}
              >
                <span className={`w-3.5 h-3.5 rounded border flex items-center justify-center flex-shrink-0 transition-colors ${
                  isSelected ? 'bg-primary border-primary' : 'border-border'
                }`}>
                  {isSelected && (
                    <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
                      <path d="M1 4L3 6L7 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </span>
                {colorMap ? (
                  <span className={`inline-flex px-1.5 py-0.5 rounded-full text-[10px] font-500 ${colorMap[opt] || 'bg-muted text-muted-foreground'}`}>
                    {opt}
                  </span>
                ) : (
                  <span>{opt}</span>
                )}
              </button>
            );
          })}
          {hasSelection && (
            <>
              <div className="border-t border-border/60 my-1" />
              <button
                type="button"
                onClick={() => { onChange([]); setOpen(false); }}
                className="w-full flex items-center gap-2 px-3 py-2 text-xs text-muted-foreground hover:text-negative transition-colors"
              >
                <X size={10} />
                Clear
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// P&L Range Slider component
interface PnlRangeProps {
  min: string;
  max: string;
  onMinChange: (v: string) => void;
  onMaxChange: (v: string) => void;
  dataMin: number;
  dataMax: number;
}

function PnlRangeFilter({ min, max, onMinChange, onMaxChange, dataMin, dataMax }: PnlRangeProps) {
  const effectiveMin = min !== '' ? Number(min) : dataMin;
  const effectiveMax = max !== '' ? Number(max) : dataMax;
  const range = dataMax - dataMin || 1;

  const leftPct = ((effectiveMin - dataMin) / range) * 100;
  const rightPct = ((effectiveMax - dataMin) / range) * 100;

  const hasFilter = min !== '' || max !== '';

  return (
    <div className="flex flex-col gap-1 min-w-[180px]">
      <label className="flex items-center gap-1 text-xs font-500 text-muted-foreground">
        <TrendingUp size={11} />
        P&amp;L Range
        {hasFilter && (
          <span className="ml-1 text-[10px] text-primary font-600">
            ${effectiveMin >= 0 ? '+' : ''}{effectiveMin.toLocaleString()} → ${effectiveMax >= 0 ? '+' : ''}{effectiveMax.toLocaleString()}
          </span>
        )}
      </label>
      <div className="flex items-center gap-2">
        <div className="relative flex-1 h-5 flex items-center">
          {/* Track */}
          <div className="absolute inset-x-0 h-1 bg-border rounded-full" />
          {/* Active range */}
          <div
            className="absolute h-1 bg-primary rounded-full"
            style={{ left: `${leftPct}%`, right: `${100 - rightPct}%` }}
          />
          {/* Min thumb */}
          <input
            type="range"
            min={dataMin}
            max={dataMax}
            step={Math.max(1, Math.round(range / 100))}
            value={effectiveMin}
            onChange={(e) => {
              const v = Number(e.target.value);
              if (v <= effectiveMax) onMinChange(String(v));
            }}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
            style={{ pointerEvents: 'auto' }}
          />
          {/* Max thumb */}
          <input
            type="range"
            min={dataMin}
            max={dataMax}
            step={Math.max(1, Math.round(range / 100))}
            value={effectiveMax}
            onChange={(e) => {
              const v = Number(e.target.value);
              if (v >= effectiveMin) onMaxChange(String(v));
            }}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-20"
            style={{ pointerEvents: 'auto' }}
          />
          {/* Visual thumbs */}
          <div
            className="absolute w-3.5 h-3.5 bg-primary border-2 border-card rounded-full shadow-md pointer-events-none z-30"
            style={{ left: `calc(${leftPct}% - 7px)` }}
          />
          <div
            className="absolute w-3.5 h-3.5 bg-primary border-2 border-card rounded-full shadow-md pointer-events-none z-30"
            style={{ left: `calc(${rightPct}% - 7px)` }}
          />
        </div>
        {hasFilter && (
          <button
            type="button"
            onClick={() => { onMinChange(''); onMaxChange(''); }}
            className="text-muted-foreground hover:text-negative transition-colors flex-shrink-0"
          >
            <X size={11} />
          </button>
        )}
      </div>
      <div className="flex justify-between text-[10px] text-muted-foreground">
        <span>${dataMin.toLocaleString()}</span>
        <span>${dataMax.toLocaleString()}</span>
      </div>
    </div>
  );
}

// R:R Range filter
interface RRRangeProps {
  min: string;
  max: string;
  onMinChange: (v: string) => void;
  onMaxChange: (v: string) => void;
}

function RRRangeFilter({ min, max, onMinChange, onMaxChange }: RRRangeProps) {
  const hasFilter = min !== '' || max !== '';
  const presets = [
    { label: '≥ 1:1', min: '1', max: '' },
    { label: '≥ 2:1', min: '2', max: '' },
    { label: '≥ 3:1', min: '3', max: '' },
    { label: 'Losers', min: '', max: '0' },
  ];

  return (
    <div className="flex flex-col gap-1">
      <label className="flex items-center gap-1 text-xs font-500 text-muted-foreground">
        <BarChart2 size={11} />
        Risk:Reward
        {hasFilter && (
          <span className="ml-1 text-[10px] text-primary font-600">
            {min !== '' ? `≥${min}:1` : ''}{min !== '' && max !== '' ? ' & ' : ''}{max !== '' ? `≤${max}:1` : ''}
          </span>
        )}
      </label>
      <div className="flex items-center gap-1 flex-wrap">
        {presets.map((p) => {
          const isActive = min === p.min && max === p.max;
          return (
            <button
              key={p.label}
              type="button"
              onClick={() => {
                if (isActive) { onMinChange(''); onMaxChange(''); }
                else { onMinChange(p.min); onMaxChange(p.max); }
              }}
              className={`px-2.5 py-1.5 rounded-lg text-xs font-500 transition-all border ${
                isActive
                  ? 'bg-primary/10 border-primary/40 text-primary' :'bg-card border-border text-muted-foreground hover:text-foreground hover:border-border/80'
              }`}
            >
              {p.label}
            </button>
          );
        })}
        <div className="flex items-center gap-1">
          <input
            type="number"
            placeholder="Min"
            value={min}
            onChange={(e) => onMinChange(e.target.value)}
            className="bg-card border border-border rounded-lg px-2 py-1.5 text-xs text-foreground outline-none focus:border-primary/50 transition-colors w-14 placeholder:text-muted-foreground"
          />
          <span className="text-xs text-muted-foreground">–</span>
          <input
            type="number"
            placeholder="Max"
            value={max}
            onChange={(e) => onMaxChange(e.target.value)}
            className="bg-card border border-border rounded-lg px-2 py-1.5 text-xs text-foreground outline-none focus:border-primary/50 transition-colors w-14 placeholder:text-muted-foreground"
          />
        </div>
      </div>
    </div>
  );
}

interface TradeLogTableProps {
  showFilters?: boolean;
  onFilterToggle?: () => void;
  refreshKey?: number;
}

export default function TradeLogTable({ showFilters: externalShowFilters, onFilterToggle, refreshKey }: TradeLogTableProps) {
  const [trades, setTrades] = useState<StoredTrade[]>([]);
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<keyof StoredTrade>('dateISO');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [page, setPage] = useState(1);
  const [perPage] = useState(8);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<ActiveFilters>(defaultFilters);
  const [filtersLoaded, setFiltersLoaded] = useState(false);
  const [viewTrade, setViewTrade] = useState<StoredTrade | null>(null);
  const [editTrade, setEditTrade] = useState<StoredTrade | null>(null);
  const [editModalOpen, setEditModalOpen] = useState(false);

  // Load persisted filters on mount (client-side only)
  useEffect(() => {
    setFilters(loadPersistedFilters());
    setFiltersLoaded(true);
  }, []);

  // Persist filters whenever they change (after initial load)
  useEffect(() => {
    if (filtersLoaded) saveFilters(filters);
  }, [filters, filtersLoaded]);

  const reloadTrades = useCallback(async () => {
    const data = await loadTradesAsync();
    setTrades(data);
  }, []);

  useEffect(() => {
    reloadTrades();
  }, [reloadTrades, refreshKey]);

  // Compute P&L bounds from actual data
  const pnlValues = trades.map((t) => t.pnl);
  const dataPnlMin = pnlValues.length ? Math.floor(Math.min(...pnlValues)) : -2000;
  const dataPnlMax = pnlValues.length ? Math.ceil(Math.max(...pnlValues)) : 2000;

  // Sync external filter toggle
  const effectiveShowFilters = externalShowFilters !== undefined ? externalShowFilters : showFilters;

  const updateFilter = <K extends keyof ActiveFilters>(key: K, value: ActiveFilters[K]) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const clearAllFilters = () => {
    setFilters(defaultFilters);
    setPage(1);
  };

  // Build active filter badges
  const activeFilterBadges: { label: string; onClear: () => void }[] = [];
  if (filters.dateFrom || filters.dateTo) {
    const label = filters.dateFrom && filters.dateTo
      ? `${filters.dateFrom} → ${filters.dateTo}`
      : filters.dateFrom ? `From ${filters.dateFrom}` : `Until ${filters.dateTo}`;
    activeFilterBadges.push({ label, onClear: () => setFilters((p) => ({ ...p, dateFrom: '', dateTo: '' })) });
  }
  filters.strategies.forEach((s) =>
    activeFilterBadges.push({ label: s, onClear: () => updateFilter('strategies', filters.strategies.filter((x) => x !== s)) })
  );
  filters.directions.forEach((d) =>
    activeFilterBadges.push({ label: d, onClear: () => updateFilter('directions', filters.directions.filter((x) => x !== d)) })
  );
  filters.outcomes.forEach((o) =>
    activeFilterBadges.push({ label: o, onClear: () => updateFilter('outcomes', filters.outcomes.filter((x) => x !== o)) })
  );
  if (filters.pnlMin !== '' || filters.pnlMax !== '') {
    const pMin = filters.pnlMin !== '' ? Number(filters.pnlMin) : dataPnlMin;
    const pMax = filters.pnlMax !== '' ? Number(filters.pnlMax) : dataPnlMax;
    activeFilterBadges.push({
      label: `P&L $${pMin.toLocaleString()} – $${pMax.toLocaleString()}`,
      onClear: () => setFilters((p) => ({ ...p, pnlMin: '', pnlMax: '' })),
    });
  }
  if (filters.rrMin !== '' || filters.rrMax !== '') {
    const label = `R:R ${filters.rrMin !== '' ? `≥${filters.rrMin}` : ''}${filters.rrMin !== '' && filters.rrMax !== '' ? ' & ' : ''}${filters.rrMax !== '' ? `≤${filters.rrMax}` : ''}`;
    activeFilterBadges.push({ label, onClear: () => setFilters((p) => ({ ...p, rrMin: '', rrMax: '' })) });
  }

  const hasActiveFilters = activeFilterBadges.length > 0;

  const filtered = trades.filter((t) => {
    if (search && !t.symbol.toLowerCase().includes(search.toLowerCase()) && !t.strategy.toLowerCase().includes(search.toLowerCase())) return false;
    if (filters.dateFrom && t.dateISO < filters.dateFrom) return false;
    if (filters.dateTo && t.dateISO > filters.dateTo) return false;
    if (filters.strategies.length > 0 && !filters.strategies.includes(t.strategy)) return false;
    if (filters.directions.length > 0 && !filters.directions.includes(t.direction)) return false;
    if (filters.outcomes.length > 0 && !filters.outcomes.includes(t.status)) return false;
    if (filters.pnlMin !== '' && t.pnl < Number(filters.pnlMin)) return false;
    if (filters.pnlMax !== '' && t.pnl > Number(filters.pnlMax)) return false;
    if (filters.rrMin !== '' && t.rr < Number(filters.rrMin)) return false;
    if (filters.rrMax !== '' && t.rr > Number(filters.rrMax)) return false;
    return true;
  });

  const sorted = [...filtered].sort((a, b) => {
    const aVal = a[sortKey];
    const bVal = b[sortKey];
    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
    }
    return sortDir === 'asc'
      ? String(aVal).localeCompare(String(bVal))
      : String(bVal).localeCompare(String(aVal));
  });

  const totalPages = Math.ceil(sorted.length / perPage);
  const paginated = sorted.slice((page - 1) * perPage, page * perPage);

  const toggleSort = (key: keyof StoredTrade) => {
    if (sortKey === key) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('desc'); }
  };

  const toggleSelect = (id: string) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };

  const toggleAll = () => {
    if (selected.size === paginated.length) setSelected(new Set());
    else setSelected(new Set(paginated.map((t) => t.id)));
  };

  const handleDeleteTrade = async (id: string, symbol: string) => {
    const updated = await deleteTradeAsync(id);
    setTrades(updated);
    setSelected((prev) => { const next = new Set(prev); next.delete(id); return next; });
    toast.success(`Trade ${symbol} deleted`);
  };

  const handleBulkDelete = async () => {
    const ids = Array.from(selected);
    const updated = await deleteTradesBulkAsync(ids);
    setTrades(updated);
    setSelected(new Set());
    toast.success(`${ids.length} trade${ids.length > 1 ? 's' : ''} deleted`);
  };

  const handleEditSaved = (_saved: StoredTrade) => {
    reloadTrades();
  };

  const handleInternalFilterToggle = () => {
    if (onFilterToggle) {
      onFilterToggle();
    } else {
      setShowFilters((v) => !v);
    }
  };

  const SortIcon = ({ col }: { col: keyof StoredTrade }) => (
    <span className="inline-flex flex-col ml-1 opacity-40">
      <ChevronUp size={8} className={sortKey === col && sortDir === 'asc' ? 'opacity-100 text-primary' : ''} />
      <ChevronDown size={8} className={sortKey === col && sortDir === 'desc' ? 'opacity-100 text-primary' : ''} />
    </span>
  );

  return (
    <>
      <div className="bg-card border border-border rounded-xl card-glow-primary">
        {/* Table header */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 p-4 border-b border-border">
          <div className="flex-1">
            <h3 className="text-sm font-600 text-foreground">Trade Log</h3>
            <p className="text-xs text-muted-foreground">{filtered.length} trades · {new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                placeholder="Search symbol or strategy…"
                className="bg-muted border border-border rounded-lg pl-8 pr-3 py-2 text-sm text-foreground placeholder:text-muted-foreground outline-none focus:border-primary/50 w-52 transition-colors"
              />
            </div>
            <button
              onClick={handleInternalFilterToggle}
              aria-label={effectiveShowFilters ? 'Hide filters' : 'Show filters'}
              className={`p-2 border rounded-lg transition-all relative ${
                effectiveShowFilters || hasActiveFilters
                  ? 'bg-primary/10 border-primary/50 text-primary' : 'bg-muted border-border text-muted-foreground hover:text-foreground hover:border-primary/50'
              }`}
            >
              <SlidersHorizontal size={14} />
              {hasActiveFilters && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-primary text-primary-foreground rounded-full text-[9px] font-700 flex items-center justify-center">
                  {activeFilterBadges.length}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Filter Bar */}
        {effectiveShowFilters && (
          <div className="border-b border-border bg-muted/20 px-4 py-4">
            <div className="flex flex-wrap gap-4 items-start">
              {/* Date Range */}
              <div className="flex flex-col gap-1 min-w-0">
                <label className="flex items-center gap-1 text-xs font-500 text-muted-foreground">
                  <Calendar size={11} />
                  Date Range
                </label>
                <div className="flex items-center gap-1.5">
                  <input
                    type="date"
                    value={filters.dateFrom}
                    onChange={(e) => updateFilter('dateFrom', e.target.value)}
                    className="bg-card border border-border rounded-lg px-2.5 py-1.5 text-xs text-foreground outline-none focus:border-primary/50 transition-colors w-36"
                  />
                  <span className="text-xs text-muted-foreground">→</span>
                  <input
                    type="date"
                    value={filters.dateTo}
                    onChange={(e) => updateFilter('dateTo', e.target.value)}
                    className="bg-card border border-border rounded-lg px-2.5 py-1.5 text-xs text-foreground outline-none focus:border-primary/50 transition-colors w-36"
                  />
                  {(filters.dateFrom || filters.dateTo) && (
                    <button
                      type="button"
                      onClick={() => setFilters((p) => ({ ...p, dateFrom: '', dateTo: '' }))}
                      className="text-muted-foreground hover:text-negative transition-colors"
                    >
                      <X size={11} />
                    </button>
                  )}
                </div>
              </div>

              {/* Strategy multi-select */}
              <MultiSelectDropdown
                label="Strategy"
                icon={<Tag size={11} />}
                options={ALL_STRATEGIES}
                selected={filters.strategies}
                onChange={(v) => updateFilter('strategies', v)}
                colorMap={strategyColors}
              />

              {/* Direction multi-select */}
              <MultiSelectDropdown
                label="Direction"
                icon={<ArrowUpDown size={11} />}
                options={ALL_DIRECTIONS}
                selected={filters.directions}
                onChange={(v) => updateFilter('directions', v)}
              />

              {/* Outcome (status) multi-select */}
              <MultiSelectDropdown
                label="Outcome"
                icon={<CheckSquare size={11} />}
                options={ALL_OUTCOMES}
                selected={filters.outcomes}
                onChange={(v) => updateFilter('outcomes', v)}
                colorMap={statusColors}
              />

              {/* P&L Range Slider */}
              {trades.length > 0 && (
                <PnlRangeFilter
                  min={filters.pnlMin}
                  max={filters.pnlMax}
                  onMinChange={(v) => updateFilter('pnlMin', v)}
                  onMaxChange={(v) => updateFilter('pnlMax', v)}
                  dataMin={dataPnlMin}
                  dataMax={dataPnlMax}
                />
              )}

              {/* R:R Range */}
              <RRRangeFilter
                min={filters.rrMin}
                max={filters.rrMax}
                onMinChange={(v) => updateFilter('rrMin', v)}
                onMaxChange={(v) => updateFilter('rrMax', v)}
              />

              {/* Clear all */}
              {hasActiveFilters && (
                <button
                  onClick={clearAllFilters}
                  className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-500 text-muted-foreground hover:text-negative border border-border hover:border-negative/40 bg-card transition-all self-end"
                >
                  <X size={11} />
                  Clear all
                </button>
              )}
            </div>
          </div>
        )}

        {/* Active Filter Badges */}
        {hasActiveFilters && (
          <div className="flex flex-wrap items-center gap-2 px-4 py-2.5 border-b border-border/60 bg-primary/[0.03]">
            <span className="text-xs text-muted-foreground font-500">Active filters:</span>
            {activeFilterBadges.map((badge, idx) => (
              <span
                key={`badge-${idx}`}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-500 bg-primary/10 text-primary border border-primary/20"
              >
                {badge.label}
                <button
                  onClick={() => { badge.onClear(); setPage(1); }}
                  className="hover:text-primary/60 transition-colors"
                >
                  <X size={10} />
                </button>
              </span>
            ))}
            <button
              onClick={clearAllFilters}
              className="text-xs text-muted-foreground hover:text-negative transition-colors ml-1"
            >
              Clear all
            </button>
          </div>
        )}

        {/* Bulk action bar */}
        {selected.size > 0 && (
          <div className="flex items-center gap-3 px-4 py-2.5 bg-primary/5 border-b border-primary/20 animate-slide-up">
            <CheckSquare size={14} className="text-primary" />
            <span className="text-sm font-500 text-primary">{selected.size} selected</span>
            <div className="flex-1" />
            <button
              onClick={handleBulkDelete}
              className="flex items-center gap-1.5 text-xs text-negative hover:text-negative/80 font-500 transition-colors"
            >
              <Trash2 size={12} />
              Delete selected
            </button>
          </div>
        )}

        {/* Table */}
        <div className="overflow-x-auto scrollbar-thin">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                <th className="w-10 px-4 py-3 text-left">
                  <input
                    type="checkbox"
                    aria-label="Select all trades on this page"
                    checked={selected.size === paginated.length && paginated.length > 0}
                    onChange={toggleAll}
                    className="accent-primary w-3.5 h-3.5"
                  />
                </th>
                {[
                  { key: 'dateISO', label: 'Date' },
                  { key: 'symbol', label: 'Symbol' },
                  { key: 'direction', label: 'Dir' },
                  { key: 'entry', label: 'Entry' },
                  { key: 'exit', label: 'Exit' },
                  { key: 'pnl', label: 'P&L ($)' },
                  { key: 'pnlPct', label: 'P&L (%)' },
                  { key: 'rr', label: 'R:R' },
                  { key: 'strategy', label: 'Strategy' },
                  { key: 'rating', label: 'Rating' },
                  { key: 'status', label: 'Status' },
                ].map((col) => (
                  <th
                    key={`th-${col.key}`}
                    onClick={() => toggleSort(col.key as keyof StoredTrade)}
                    className="px-3 py-3 text-left text-xs font-500 text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground transition-colors whitespace-nowrap select-none"
                  >
                    {col.label}
                    <SortIcon col={col.key as keyof StoredTrade} />
                  </th>
                ))}
                <th className="px-3 py-3 text-left text-xs font-500 text-muted-foreground uppercase tracking-wider w-24">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {paginated.length === 0 ? (
                <tr>
                  <td colSpan={13} className="px-4 py-10 text-center text-sm text-muted-foreground">
                    No trades match the current filters.
                  </td>
                </tr>
              ) : (
                paginated.map((trade, idx) => (
                  <tr
                    key={trade.id}
                    className={`border-b border-border/50 transition-colors group ${
                      selected.has(trade.id) ? 'bg-primary/5' : idx % 2 === 0 ? 'bg-transparent' : 'bg-muted/20'
                    } hover:bg-muted/40`}
                  >
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        aria-label={`Select trade ${trade.symbol} on ${trade.date}`}
                        checked={selected.has(trade.id)}
                        onChange={() => toggleSelect(trade.id)}
                        className="accent-primary w-3.5 h-3.5"
                      />
                    </td>
                    <td className="px-3 py-3 text-xs text-muted-foreground font-mono-nums whitespace-nowrap">{trade.date}</td>
                    <td className="px-3 py-3">
                      <div className="flex items-center gap-2">
                        <span className="font-600 text-foreground font-mono-nums">{trade.symbol}</span>
                        {trade.hasScreenshot && (
                          <ImageIcon size={11} className="text-muted-foreground" />
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-3">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-600 ${
                        trade.direction === 'Long' ? 'bg-positive-subtle text-positive' : 'bg-negative-subtle text-negative'
                      }`}>
                        {trade.direction}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-xs text-foreground font-mono-nums">${trade.entry.toFixed(2)}</td>
                    <td className="px-3 py-3 text-xs text-foreground font-mono-nums">${trade.exit.toFixed(2)}</td>
                    <td className="px-3 py-3">
                      <span className={`text-sm font-600 font-mono-nums ${trade.pnl >= 0 ? 'text-positive' : 'text-negative'}`}>
                        {trade.pnl >= 0 ? '+' : ''}${trade.pnl.toLocaleString()}
                      </span>
                    </td>
                    <td className="px-3 py-3">
                      <span className={`text-xs font-500 font-mono-nums ${trade.pnlPct >= 0 ? 'text-positive' : 'text-negative'}`}>
                        {trade.pnlPct >= 0 ? '+' : ''}{trade.pnlPct.toFixed(2)}%
                      </span>
                    </td>
                    <td className="px-3 py-3">
                      <span className={`text-xs font-500 font-mono-nums ${trade.rr >= 1.5 ? 'text-positive' : trade.rr >= 0 ? 'text-warning' : 'text-negative'}`}>
                        {trade.rr >= 0 ? '1:' : ''}{trade.rr.toFixed(1)}
                      </span>
                    </td>
                    <td className="px-3 py-3">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-500 ${strategyColors[trade.strategy] || 'bg-muted text-muted-foreground'}`}>
                        {trade.strategy}
                      </span>
                    </td>
                    <td className="px-3 py-3">
                      <StarRating rating={trade.rating} />
                    </td>
                    <td className="px-3 py-3">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-500 ${statusColors[trade.status]}`}>
                        {trade.status}
                      </span>
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          title="View trade details"
                          onClick={() => setViewTrade(trade)}
                          className="p-1.5 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                        >
                          <Eye size={13} />
                        </button>
                        <button
                          title="Edit trade"
                          onClick={() => { setEditTrade(trade); setEditModalOpen(true); }}
                          className="p-1.5 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                        >
                          <Edit3 size={13} />
                        </button>
                        <button
                          title="Delete trade — this cannot be undone"
                          onClick={() => handleDeleteTrade(trade.id, trade.symbol)}
                          className="p-1.5 rounded-md hover:bg-negative-subtle text-muted-foreground hover:text-negative transition-colors"
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-4 py-3 border-t border-border">
          <p className="text-xs text-muted-foreground">
            Showing {sorted.length === 0 ? 0 : (page - 1) * perPage + 1}–{Math.min(page * perPage, sorted.length)} of {sorted.length} trades
          </p>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page === 1}
              className="px-2.5 py-1.5 text-xs rounded-md bg-muted text-muted-foreground hover:text-foreground disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Prev
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
              <button
                key={`page-${p}`}
                onClick={() => setPage(p)}
                className={`w-7 h-7 text-xs rounded-md transition-colors ${
                  p === page
                    ? 'bg-primary text-primary-foreground font-600'
                    : 'bg-muted text-muted-foreground hover:text-foreground'
                }`}
              >
                {p}
              </button>
            ))}
            <button
              onClick={() => setPage(Math.min(totalPages, page + 1))}
              disabled={page === totalPages || totalPages === 0}
              className="px-2.5 py-1.5 text-xs rounded-md bg-muted text-muted-foreground hover:text-foreground disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {/* View Detail Modal */}
      <TradeDetailModal
        trade={viewTrade}
        onClose={() => setViewTrade(null)}
        onEdit={(t) => { setEditTrade(t); setEditModalOpen(true); }}
      />

      {/* Edit Modal */}
      <LogTradeModal
        open={editModalOpen}
        onClose={() => { setEditModalOpen(false); setEditTrade(null); }}
        editTrade={editTrade}
        onSaved={handleEditSaved}
      />
    </>
  );
}